import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { build, buildParams, pool } from '../../../../../common/classes/request-builder';
import { ReactiveFormChangedField, ReactiveFormValue } from '../../../../../common/components/reactive-form-builder/interfaces/reactive-form-change.interface';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { BaseForm } from '../../../../base-form';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { RequestHandler } from '../../../../../service/OffService/request-handler';
import { TareaEdificiosModel } from '../../../../../models/tareas/tarea-edificios.model';
import { TareasEdificiosFormDefinition } from './form-control/form-definition';

@Component({
    selector: 'app-tareas-edificios-form',
    standalone: false,
    templateUrl: './tareas-edificios-form.component.html',
    styleUrls: ['./tareas-edificios-form.component.scss'],
})
export class TareasEdificiosFormComponent extends BaseForm<TareaEdificiosModel> implements OnInit {
    public form!: TareasEdificiosFormDefinition;
    public formReady = false;
    public loadingData = true;
    public getRequest!: RequestHandler<TareaEdificiosModel[]>;
    public override model: TareaEdificiosModel = {};

    private lastReloadedClientDropdownId = '';
    private isReloadingClientDropdowns = false;

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public fincasApi: FincasApiService,
        public clientesApi: ClientesApiService,
        public trabajadoresApi: TrabajadoresApiService,
        public tareasApi: TareasApiService,
        public override router: Router,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.edificios.PUT,
            tareasApi.edificios.POST,
            'edificios',
            'Actualizar Tarea Edificios',
            'Realizar Tarea Edificios',
            'Duplicar Tarea Edificios',
        );
        this.getRequest = tareasApi.edificios.GET;
    }

    public ngOnInit(): void {
        this.dashboard.clientChanges$.subscribe(() => {
            this.fincasApi.fincasGET.unsuscribe();
            
        });

        this.softInit(this.getType());
        void this.setupForm();
    }

    public formChanges(
        formModel: ReactiveFormValue<TareaEdificiosModel>,
        lastChangedFieldName: ReactiveFormChangedField<TareaEdificiosModel>,
    ): void {
        const changedFieldName = String(lastChangedFieldName);

        this.form.model = {
            ...this.form.model,
            ...formModel,
        };

        this.syncCalculatedHours(changedFieldName);

        if (changedFieldName === 'mantenimiento') {
            this.form.updateMantenimientoLabel();
            this.dropdownLoadTrigger.next();
        }

        if (changedFieldName === 'id_cliente') {
            this.onClientChanged();
        }

        if (changedFieldName === 'id_finca' || changedFieldName === 'id_sector') {
            this.dropdownLoadTrigger.next();
        }
    }

    public onAfterSend(): void {
        StorageManager.saveSessionDataDonJardin(
            this.form.model.id_finca ?? '',
            this.form.model.hora_inicio ?? '',
            this.form.model.hora_fin?.toString() ?? '',
        );
    }

    private async setupForm(): Promise<void> {
        this.model = (this.getModel() ?? {}) as TareaEdificiosModel;
        this.form = new TareasEdificiosFormDefinition(this.getType(), this.model);

        await this.initFormData();

        this.formReady = true;
    }

    private async initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.dropdownsFinalized = false;

        const buildsRequest = [
            build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', true),
            build(this.clientesApi.clientesGET, this.form.lists.clientes, { or: ['razon_social', 'nombre'] }),
            build(
                this.trabajadoresApi.trabajadoresGET,
                this.form.lists.trabajadores,
                (it: { nombre?: string; apellidos?: string }) => `${it.nombre ?? ''} ${it.apellidos ?? ''}`.trim(),
                true,
                true,
            ),
            
        ];

        return pool(buildsRequest, () => this.dropdownLoadTrigger.next())
            .then(() => {
                this.loadingData = false;
                this.dropdownsFinalized = true;
                this.dropdownLoadTrigger.next();

                return true;
            })
            .catch((error: unknown) => {
                this.loadingData = false;
                this.dropdownsFinalized = true;
                this.dropdownLoadTrigger.next();
                console.error('Error loading MTO reactive form data.', error);

                return true;
            });
    }

    private syncCalculatedHours(changedFieldName: string): void {
        const updates = this.form.getHourUpdatesForChangedField(changedFieldName);
        const updateEntries = Object.entries(updates);

        if (updateEntries.length === 0) {
            return;
        }

        this.form.model = {
            ...this.form.model,
            ...updates,
        };

        this.formBuilderRef?.updateForm(updates as Partial<TareaEdificiosModel>, true);
    }

    private onClientChanged(): void {
        const selectedClientId = this.getReactiveValueId(this.form.model.id_cliente);

        if (!selectedClientId || this.isReloadingClientDropdowns || selectedClientId === this.lastReloadedClientDropdownId) {
            return;
        }

        this.isReloadingClientDropdowns = true;
        this.lastReloadedClientDropdownId = selectedClientId;
        this.form.model.id_finca = null;

        pool([
            buildParams(this.fincasApi.fincasGET, { id_cliente: selectedClientId }, this.form.lists.fincas, 'nombre', true),
            
        ], () => this.dropdownLoadTrigger.next())
            .then(() => {
                this.form.lists.fincas.filtered = this.form.lists.fincas.values;
                this.isReloadingClientDropdowns = false;
                this.dropdownLoadTrigger.next();

                return true;
            })
            .catch((error: unknown) => {
                this.isReloadingClientDropdowns = false;
                this.dropdownLoadTrigger.next();
                console.error('Error reloading MTO client dropdowns.', error);
            });
    }

    private getReactiveValueId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '');
        }

        return String(value ?? '');
    }
}
